import { reactive } from "vue";
import { ClipQueue } from "./clipQueue";
import type { Pet } from "../core/pet";
import { loadSwfClip, type LoadedSwfClip } from "../core/swfClipAnimation";

export type SwfClipPreloadStatus = "idle" | "loading" | "done";

export const swfClipPreloadState = reactive<{
    status: SwfClipPreloadStatus;
    total: number;
    done: number;
    failed: number;
}>({
    status: "idle",
    total: 0,
    done: 0,
    failed: 0,
});

const queue = new ClipQueue();
const completed = new Set<string>();
interface SkinTask {
    priority: number;
    status: "loading" | "done" | "failed";
    clip?: LoadedSwfClip;
    promise: Promise<void>;
    cancelled: boolean;
}
const scheduled = new Map<number, SkinTask>();

function updateProgress() {
    const tasks = [...scheduled.values()].filter((task) => !task.cancelled);
    swfClipPreloadState.total = tasks.length;
    swfClipPreloadState.done = tasks.filter((task) => task.status === "done").length;
    swfClipPreloadState.failed = tasks.filter((task) => task.status === "failed").length;
    swfClipPreloadState.status = tasks.some((task) => task.status === "loading")
        ? "loading" : tasks.length ? "done" : "idle";
}
let generation = 0;

export function collectSwfClipSkinIds(pets: Pet[]): number[] {
    const ids: number[] = [];
    const seen = new Set<number>();
    const skins = [
        ...pets.map((pet) => pet.当前皮肤),
        ...pets.flatMap((pet) => pet.皮肤组),
    ];
    for (const skin of skins) {
        if (seen.has(skin.id)) continue;
        seen.add(skin.id);
        ids.push(skin.id);
    }
    return ids;
}

export function resetSwfClipPreload() {
    generation += 1;
    queue.cancelWhere(() => true);
    scheduled.clear();
    swfClipPreloadState.status = "idle";
    swfClipPreloadState.total = 0;
    swfClipPreloadState.done = 0;
    swfClipPreloadState.failed = 0;
}

async function download(url: string, priority: number, currentGeneration: number) {
    if (completed.has(url)) return;
    await queue.request(url, priority, async (signal) => {
        if (currentGeneration !== generation) throw new DOMException("Stale session", "AbortError");
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (response.body) {
            const reader = response.body.getReader();
            while (!(await reader.read()).done) { /* 流式消费，避免保留整份图集 */ }
        } else await response.arrayBuffer();
        if (currentGeneration === generation) completed.add(url);
    });
}

export async function loadSwfClipAtlas(url: string) {
    await download(url, 0, generation).catch(() => {});
    const { Assets } = await import("pixi.js");
    return Assets.load(url);
}

/** 删除被 ban 的后台任务，共享中的展示资源保持加载。 */
export function retainSwfClipSkins(pets: Pet[]) {
    const keep = new Set(collectSwfClipSkinIds(pets));
    for (const [id, task] of scheduled) {
        if (keep.has(id) || task.priority === 0) continue;
        task.cancelled = true;
        scheduled.delete(id);
    }
    const owned = new Set([...scheduled.values()].flatMap((task) => task.clip?.atlases ?? []));
    queue.cancelWhere((url, priority) => priority > 0 && !owned.has(url));
    updateProgress();
}

export async function warmSwfClipCache(pets: Pet[], priority = 3, allSkins = false): Promise<void> {
    const currentGeneration = generation;
    const ids = allSkins ? collectSwfClipSkinIds(pets) : [...new Set(pets.map((pet) => pet.当前皮肤.id))];
    const requests = ids.map((id) => {
        const existing = scheduled.get(id);
        if (existing && priority >= existing.priority) return existing.promise;
        if (existing) {
            existing.priority = priority;
            if (existing.clip && existing.status === "loading") {
                for (const url of existing.clip.atlases) void download(url, priority, currentGeneration).catch(() => {});
            }
            if (existing.status !== "failed") {
                return existing.promise.then(async () => {
                    if (priority <= 1 && existing.clip && !existing.cancelled && generation === currentGeneration) {
                        for (const url of existing.clip.atlases) await loadSwfClipAtlas(url);
                    }
                });
            }
        }
        const task: SkinTask = { priority, status: "loading", promise: Promise.resolve(), cancelled: false };
        scheduled.set(id, task);
        task.promise = (async () => {
            try {
                const clip = await loadSwfClip("", id);
                if (task.cancelled || generation !== currentGeneration) return;
                task.clip = clip;
                await Promise.all(clip.atlases.map((url) => download(url, task.priority, currentGeneration)));
                task.status = "done";
                if (task.priority <= 1 && !task.cancelled && generation === currentGeneration) {
                    for (const url of clip.atlases) await loadSwfClipAtlas(url);
                }
            } catch (error) {
                task.status = "failed";
                if (!task.cancelled && generation === currentGeneration) console.warn(`[swfclip-preload] ${id}`, error);
            } finally {
                if (generation === currentGeneration) updateProgress();
            }
        })();
        return task.promise;
    });
    updateProgress();
    await Promise.all(requests).catch((error) => console.warn("[swfclip-preload] 解码失败", error));
}
